import { Button, Drawer, Flex, Descriptions, Tag, Divider, Table, Space, Typography, message } from 'antd';
import { FileTextOutlined, CommentOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import React, {  useState, useMemo } from 'react';
import dayjs from 'dayjs';
import LegacyAPI from '../../restAPI/LegacyAPI';

const { Title, Text } = Typography;

const ArchivedMeetingDetails = ({ open, onClose, meetingData , companyId, token }) => {
  const [openPaperDrawer, setOpenPaperDrawer] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [downloading, setDownloading] = useState(false);

  
  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return dayjs(dateTimeString).format('h:mm A');
  };


  const transformMeetingData = (apiData) => {    
    if (!apiData) return null;

    const scheduleDateTime = apiData.scheduleDateTime;
    const scheduleDate = scheduleDateTime && dayjs(scheduleDateTime).isValid()
      ? dayjs(scheduleDateTime).format('YYYY-MM-DD')
      : '';
    const scheduleTime = scheduleDateTime && dayjs(scheduleDateTime).isValid()
      ? formatTime(scheduleDateTime)
      : '';

    
    const transformedPapers = (apiData.papers || []).map((paper, index) => {
      const documents = (paper.documents || []).map((doc, docIndex) => ({
        key: doc.documentId || docIndex,
        documentId: doc.documentId,
        documentName: doc.documentName || '',
        comments: doc.documentComments || 0,
      }));

      const commentsCount = documents.reduce((sum, doc) => sum + (doc.comments || 0), 0);
      const pdfCount = documents.length;

      return {
        key: paper.paperId || index,
        paperId: paper.paperId,
        paperName: paper.paperName || '',
        category: paper.paperCategoryName || '',
        paperCategory: paper.paperCategoryName || '',
        paperCode: paper.paperCode || '',
        paperNo: paper.paperNo || null,
        commentsCount,
        pdfCount,
        status: paper.status || '',
        committee: apiData.committeeName || '',
        meeting: apiData.meetingName || '',
        paperComment: paper.comment || '',
        comment: paper.comment || '',
        documents,
      };
    });

    return {
      committee: apiData.committeeName || '',
      meetingName: apiData.meetingName || '',
      venue: apiData.venue || '',
      scheduleDate,
      scheduleTime,
      comment: apiData.comment || '',
      meetingStatus: apiData.meetingStatus || '',
      isActive: apiData.isMeetingActive || false,
      minuteName: apiData.meetingMinuteName || '',
      minute: apiData.meetingMinutePath || '',
      papers: transformedPapers,
    };
  };

 
  const showPaperDrawer = (record) => {
    setSelectedPaper(record);
    setOpenPaperDrawer(true);
  };

  const onClosePaperDrawer = () => {
    setOpenPaperDrawer(false);
    setSelectedPaper(null);
  };

  const handleViewPDF = async (doc) => {
    if (!selectedPaper?.paperId || !doc?.documentId) {
      message.error('Document information is missing');
      return;
    }

    if (!companyId || !token) {
      message.error('Authentication information is missing');
      return;
    }

    setDownloading(true);
    try {
      const response = await LegacyAPI.downloadLegacyPaperDocument(
        companyId,
        token,
        selectedPaper.paperId,
        doc.documentId
      );

      if (response.status === 200) {
        const fileType = response.headers['content-type'] || 'application/pdf';
        const blob = new Blob([response.data], { type: fileType });
        const url = window.URL.createObjectURL(blob);
                
        window.open(url, '_blank');
                
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        message.error('Failed to load PDF document');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to load PDF document');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadMeetingMinute = async () => {
  try {
    const { data } = await LegacyAPI.downloadMeetingMinute(
      companyId,
      token,
      meetingData.meetingId
    );
    
    const blob = new Blob([data], { type: 'application/pdf' });     
    const url = window.URL.createObjectURL(blob);
    
    window.open(url, '_blank');

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);

  } catch (error) {
    console.error('Failed to open meeting minute:', error);
  }
};

  
  const displayData = useMemo(() => {
    if (meetingData) {
      return transformMeetingData(meetingData);
    }    
  }, [meetingData]);

  const columns = [
    {
      title: 'Paper Name',
      dataIndex: 'paperName',
      key: 'paperName',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Paper Code',
      dataIndex: 'paperCode',
      key: 'paperCode',
    },
    {
      title: 'Paper No',
      dataIndex: 'paperNo',
      key: 'paperNo',
    },
    {
      title: 'Comments',
      dataIndex: 'commentsCount',
      key: 'commentsCount',
      render: (count) => (
        <Space>
          <CommentOutlined />
          <Text>{count}</Text>
        </Space>
      ),
    },
    {
      title: 'PDFs',
      dataIndex: 'pdfCount',
      key: 'pdfCount',
      render: (count) => (
        <Space>
          <FilePdfOutlined style={{ color: '#d4380d' }} />
          <Text>{count}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'Publish' ? 'success' : 'warning'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'View',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" icon={<EyeOutlined />} onClick={() => showPaperDrawer(record)} />
        </Space>
      ),
    },
  ];

  return (
    <>
    <Drawer
      title="Archived Meeting Details"
      closable={{ 'aria-label': 'Close Button' }}
      onClose={onClose}
      open={open}
      width={1100}
      footer={[
        <Flex justify='flex-end' align='center' gap='large' key="footer">
          <Button onClick={onClose}>
            Close
          </Button>
        </Flex>
      ]}
    >
      <Descriptions column={1} colon={false} layout="horizontal">
        <Descriptions.Item label={<Text strong>Committee: </Text>}>
          <Space>
            <Text>{displayData?.committee}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Meeting Name: </Text>}>
          <Text strong style={{ fontSize: '16px' }}>{displayData?.meetingName}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Venue: </Text>}>
          <Space>
            <Text>{displayData?.venue}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Schedule Date & Time: </Text>}>
          <Space>
            <Text>{displayData?.scheduleDate}</Text>
            <Text>{displayData?.scheduleTime}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Comment: </Text>}>
          <Space>
            <Text>{displayData?.comment}</Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Meeting Status: </Text>}>
          <Tag icon={<FileTextOutlined />} color="processing">
            {displayData?.meetingStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Active: </Text>}>
          <Space>
            <Tag color={displayData?.isActive ? 'success' : 'default'}>
              {displayData?.isActive ? 'Yes' : 'No'}
            </Tag>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Minute Name: </Text>}>
          <Text strong style={{ fontSize: '16px' }}>{displayData?.minuteName}</Text>
        </Descriptions.Item>
        <Descriptions.Item label={<Text strong>Minute: </Text>}>
          <Text style={{cursor:'pointer'}} type="secondary" onClick={handleDownloadMeetingMinute}>{displayData?.minute}</Text>
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      
      <Title level={5}>Submitted Papers</Title>
      <Table
        columns={columns}
        dataSource={displayData?.papers}
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: 800 }}
      />
    </Drawer>

    
    <Drawer
      title="Papers"
      closable={{ 'aria-label': 'Close Button' }}
      onClose={onClosePaperDrawer}
      open={openPaperDrawer}
      width={800}
      footer={[
        <Flex justify='flex-end' align='center' gap='large' key="footer">
          <Button onClick={onClosePaperDrawer}>
            Close
          </Button>
        </Flex>
      ]}
    >
      {selectedPaper && (
        <>
          <Descriptions column={1} colon={false} layout="horizontal">
            <Descriptions.Item label={<Text strong>Committee: </Text>}>
              <Space>
                <Text>{selectedPaper.committee || displayData?.committee}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Meeting: </Text>}>
              <Space>
                <Text>{selectedPaper.meeting || '-'}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Paper Category: </Text>}>
              <Space>
                <Text>{selectedPaper.paperCategory || selectedPaper.category}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Paper Name: </Text>}>
              <Text type="secondary">{selectedPaper.paperName || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Paper Code: </Text>}>
              <Space>
                <Text>{selectedPaper.paperCode || '-'}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Paper No: </Text>}>
              <Text strong style={{ fontSize: '16px' }}>{selectedPaper.paperNo || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Comment: </Text>}>
              <Space>
                <Text>{selectedPaper.paperComment || selectedPaper.comment || '-'}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={<Text strong>Paper Status: </Text>}>
              <Tag icon={<FileTextOutlined />} color="processing">
                {selectedPaper.status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <Divider />
          
          <Title level={5}>Documents</Title>
          <Table
            columns={[
              {
                title: 'Document Name',
                dataIndex: 'documentName',
                key: 'documentName',
              },
              {
                title: 'Number of Comments',
                dataIndex: 'comments',
                key: 'comments',
                align: 'center',
                render: (count) => (
                  <Space>
                    <CommentOutlined />
                    <Tag color={count > 0 ? 'red' : 'default'}>{count}</Tag>
                  </Space>
                ),
              },
              {
                title: 'View Pdf',
                key: 'view',
                align: 'center',
                render: (_, doc) => (
                  <Button 
                    type="link" 
                    icon={<EyeOutlined />} 
                    onClick={() => handleViewPDF(doc)}                    
                  >
                    View PDF
                  </Button>
                ),
              },
            ]}
            dataSource={selectedPaper.documents || []}
            pagination={false}
            bordered
            size="middle"
            rowHoverable
            locale={{ emptyText: 'No documents available' }}
          />
        </>
      )}
    </Drawer>
    </>
  );
};

export default ArchivedMeetingDetails;

